import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DiagnosisView, PreauthGateItem, PreauthRowState } from '@wodi/shared';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  Notice,
  Tag,
  TextArea,
  TextField,
  type TagState,
} from '@wodi/ui';
import { useState } from 'react';

import { askPreauth, checkPreauthStatus, overridePreauth } from '../api/claims.ts';
import { ApiError } from '../api/client.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { SHAPE as SHA_APPROVAL_TAG } from '../sha/approval-tag.tsx';
import { toasts } from '../toasts.ts';
import styles from './consultation.module.css';

// SHA approval on the order screens (docs/plans/phase1-sha.md §7.5, §9.3): each ordered
// service on an SHA visit shows where it stands with SHA in words, and offers "Ask SHA for
// approval" and, for a senior clinician, going ahead without it (open question C1,
// provisional: TODO(clinical-verify)). "List not loaded" is its own state and never reads as
// "not needed".

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/** The tag: a short state in words; the shape is secondary (DESIGN.md §7). */
function tagOf(item: PreauthGateItem): { words: string; state: TagState } | null {
  const labels: Record<PreauthRowState, string | null> = {
    not_required: null,
    requirement_unknown: 'Pre-authorisation list not loaded',
    needs_request: 'Needs approval from SHA',
    waiting:
      item.request?.state === 'queued' ? 'Waiting to send to SHA' : 'Waiting for SHA approval',
    request_failed: 'Couldn’t ask SHA',
    approved: 'SHA approved',
    declined: 'SHA declined',
    overridden: 'Going ahead without SHA approval',
  };
  const words = labels[item.state];
  if (words === null) return null;
  return { words, state: SHA_APPROVAL_TAG[item.state] };
}

/** The sentence under the tag: SHA's words, what went wrong, and what to do next. */
function detailOf(item: PreauthGateItem): string | null {
  switch (item.state) {
    case 'requirement_unknown':
      return 'Wodi can’t tell whether SHA must approve this until the list is loaded in Settings > SHA.';
    case 'request_failed':
      return `${item.words} Ask SHA again. A senior clinician may go ahead without approval.`;
    case 'declined':
      return `${item.words}. Choose another service, or talk to the patient about paying cash.`;
    case 'overridden':
      return item.override === null
        ? null
        : `By ${item.override.by}. ${item.request === null ? '' : item.request.sentence}`.trim();
    case 'waiting':
    case 'approved':
      return item.words === tagOf(item)?.words ? null : item.words;
    default:
      return null;
  }
}

export interface ShaApprovalProps {
  item: PreauthGateItem;
  /** What the order is, for the dialogs. */
  serviceName: string;
  visitId: string;
  diagnoses: readonly DiagnosisView[];
  mayRequest: boolean;
  mayOverride: boolean;
  /** Open visit and a connection to Wodi. */
  canAct: boolean;
}

/** One order's SHA approval: the state in words and the actions it allows. */
export function ShaApproval({
  item,
  serviceName,
  visitId,
  diagnoses,
  mayRequest,
  mayOverride,
  canAct,
}: ShaApprovalProps) {
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [overriding, setOverriding] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['visits', visitId, 'orders'] });
  const override = useMutation({
    mutationFn: (reason: string) => overridePreauth(item.request?.id ?? '', reason),
    onSuccess: async () => {
      setOverriding(false);
      await refresh();
      toasts.show({ title: 'Going ahead without SHA approval' });
    },
  });
  const check = useMutation({
    mutationFn: () => checkPreauthStatus(item.request?.id ?? ''),
    onSuccess: refresh,
  });
  const tag = tagOf(item);
  if (tag === null) return null;
  const detail = detailOf(item);
  const mayAsk =
    mayRequest && canAct && (item.state === 'needs_request' || item.state === 'request_failed');
  const mayGoAhead =
    mayOverride &&
    canAct &&
    item.request !== null &&
    (item.state === 'waiting' || item.state === 'request_failed');
  const mayCheck = canAct && item.state === 'waiting' && item.request?.state === 'submitted';
  return (
    <span className={styles.itemMain}>
      <span className={styles.actions}>
        <Tag state={tag.state}>{tag.words}</Tag>
        {mayAsk ? (
          <Button
            dense
            onPress={() => {
              setAsking(true);
            }}
          >
            {item.state === 'request_failed' ? 'Ask SHA again' : 'Ask SHA for approval'}
          </Button>
        ) : null}
        {mayCheck ? (
          <Button
            dense
            variant="quiet"
            isPending={check.isPending}
            pendingLabel="Checking…"
            onPress={() => {
              check.mutate();
            }}
          >
            Check now
          </Button>
        ) : null}
        {mayGoAhead ? (
          <Button
            dense
            variant="quiet"
            onPress={() => {
              setOverriding(true);
            }}
          >
            Go ahead without approval
          </Button>
        ) : null}
      </span>
      {detail === null ? null : (
        <span className={styles.itemMeta}>
          {item.state === 'declined' ? <strong>{detail}</strong> : detail}
        </span>
      )}
      {check.error === null ? null : (
        <span className={styles.itemMeta}>
          {errorText(check.error, 'SHA couldn’t be asked just now. Wodi keeps checking.')}
        </span>
      )}
      {asking ? (
        <AskShaDialog
          item={item}
          serviceName={serviceName}
          visitId={visitId}
          diagnoses={diagnoses}
          onClose={() => {
            setAsking(false);
          }}
        />
      ) : null}
      <ReasonDialog
        title="Go ahead without SHA approval"
        isOpen={overriding}
        onOpenChange={setOverriding}
        confirmLabel="Go ahead without approval"
        pendingLabel="Recording…"
        isPending={override.isPending}
        error={
          override.error === null
            ? null
            : errorText(override.error, 'It could not be recorded. Try again.')
        }
        onConfirm={(reason) => {
          override.mutate(reason);
        }}
      >
        <p>
          {`SHA hasn’t approved ${serviceName}. Going ahead is recorded with your name and reason, and the claim shows that this line went ahead without SHA approval.`}
        </p>
      </ReasonDialog>
    </span>
  );
}

const JUSTIFICATION_MIN = 5;
const JUSTIFICATION_MAX = 2000;

function diagnosisWords(d: DiagnosisView): string {
  const text = d.coding?.display ?? d.clinicianText ?? 'Diagnosis';
  return d.coding === null ? `${text} (not coded)` : `${d.coding.code} ${text}`;
}

/** "Ask SHA for approval": service, quantity, diagnoses and the clinician's justification. */
function AskShaDialog({
  item,
  serviceName,
  visitId,
  diagnoses,
  onClose,
}: {
  item: PreauthGateItem;
  serviceName: string;
  visitId: string;
  diagnoses: readonly DiagnosisView[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const current = diagnoses.filter((d) => d.status === 'current');
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const [quantity, setQuantity] = useState('1');
  const [justification, setJustification] = useState('');
  const [tried, setTried] = useState(false);
  const [requestId] = useState(() => crypto.randomUUID());
  const count = Number(quantity);
  const quantityOk = /^\d+$/.test(quantity.trim()) && count >= 1 && count <= 1000;
  const text = justification.trim();
  const justificationOk = text.length >= JUSTIFICATION_MIN && text.length <= JUSTIFICATION_MAX;
  const ask = useMutation({
    mutationFn: () =>
      askPreauth(visitId, {
        clientRequestId: requestId,
        source: item.source,
        diagnosisIds: [...chosen],
        justification: text,
        quantity: count,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['visits', visitId, 'orders'] });
      toasts.show({ title: 'Approval requested' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Ask SHA for approval"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.itemTitle}>{serviceName}</p>
      <TextField
        label="Quantity"
        inputMode="numeric"
        value={quantity}
        onChange={setQuantity}
        {...(tried && !quantityOk ? { errorMessage: 'Enter a whole number from 1 to 1000.' } : {})}
      />
      {current.length === 0 ? (
        <Notice tone="info">
          Record a diagnosis first. SHA is asked to approve a service for a diagnosis.
        </Notice>
      ) : (
        <fieldset className={styles.form}>
          <legend className={styles.subheading}>Diagnoses this is for</legend>
          {current.map((d) => (
            <Checkbox
              key={d.id}
              isSelected={chosen.has(d.id)}
              onChange={(on) => {
                setChosen((prev) => {
                  const next = new Set(prev);
                  if (on) next.add(d.id);
                  else next.delete(d.id);
                  return next;
                });
              }}
            >
              {diagnosisWords(d)}
            </Checkbox>
          ))}
          {tried && chosen.size === 0 ? (
            <Notice tone="error">Tick at least one diagnosis.</Notice>
          ) : null}
        </fieldset>
      )}
      <TextArea
        label="Clinical justification"
        description="Why the patient needs this service. Sent to SHA with the request."
        value={justification}
        onChange={setJustification}
        rows={4}
        {...(tried && !justificationOk
          ? {
              errorMessage: `Write the justification in ${String(JUSTIFICATION_MIN)} to ${String(JUSTIFICATION_MAX)} characters.`,
            }
          : {})}
      />
      {ask.error === null ? null : (
        <Notice tone="error">{errorText(ask.error, 'SHA could not be asked. Try again.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={current.length === 0}
          isPending={ask.isPending}
          pendingLabel="Asking…"
          onPress={() => {
            setTried(true);
            if (quantityOk && justificationOk && chosen.size > 0) ask.mutate();
          }}
        >
          Ask SHA for approval
        </Button>
      </DialogActions>
    </Dialog>
  );
}
